import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { GoogleMap, Polyline, LoadScriptNext, Marker } from "@react-google-maps/api";
import { grey } from "@mui/material/colors";
import {
  Box,
  Typography,
  Slide,
  Grid,
  Card,
  Paper,
  AppBar,
  Container,
  Toolbar,
  Button,
} from "@mui/material";
import { styled } from "@mui/material/styles";

import { ObtenerUnViajeGPS, ObtenerViajesPorLatamDocId } from "../../APIS/api";
import Logo from "../Imagenes/logo_morado_texto_al_lado.png";

const containerStyle = {
  width: "100%",
  height: "90vh",
};

const HoverableCard = styled(Card)(({ theme, isSelected }) => ({
  boxShadow: theme.shadows[4],
  borderRadius: theme.shape.borderRadius,
  width: "auto",
  margin: 10,
  color: theme.palette.text.primary,
  backgroundColor: isSelected ? "#333" : grey[200],
  transition: "background-color 0.3s ease-in-out",
  "&:hover": {
    backgroundColor: "#1976d2",
    color: "#fff",
    cursor: "pointer",
  },
  ...(isSelected && {
    backgroundColor: "#1976d2",
    color: "#fff",
  }),
}));

// Formatear timestamps de Firestore o fechas JS
const formatDateTime = (ts) => {
  if (!ts) return "";
  const date = ts.toDate ? ts.toDate() : new Date(ts);
  return date.toLocaleString("es-CL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// Helpers para distancia (Haversine)
const toRad = (value) => (value * Math.PI) / 180;

const distanciaEntrePuntosKm = (p1, p2) => {
  const R = 6371; // radio de la Tierra en km
  const dLat = toRad(p2.lat - p1.lat);
  const dLng = toRad(p2.lng - p1.lng);
  const lat1 = toRad(p1.lat);
  const lat2 = toRad(p2.lat);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.sin(dLng / 2) * Math.sin(dLng / 2) * Math.cos(lat1) * Math.cos(lat2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

const distanciaTotalKm = (path) => {
  if (!Array.isArray(path) || path.length < 2) return 0;
  let total = 0;
  for (let i = 1; i < path.length; i++) {
    total += distanciaEntrePuntosKm(path[i - 1], path[i]);
  }
  return total;
};

export default function SeguimientoUnServicio() {
  // viajeId en la URL es el latamDocId: "LATAM-28"
  const { viajeId } = useParams();

  const [viajesLista, setViajesLista] = useState([]); // LATAM-28-0, LATAM-28-6...
  const [viajeSeleccionadoId, setViajeSeleccionadoId] = useState(null);

  const [viaje, setViaje] = useState(null); // datos + gps del viaje seleccionado
  const [polylinePath, setPolylinePath] = useState([]); // trazado del viaje seleccionado
  const [distanciaKm, setDistanciaKm] = useState(0);   // km del viaje seleccionado

  const [center, setCenter] = useState({
    lat: -33.38653483189307,
    lng: -70.79489558751244,
  });
  const [zoom, setZoom] = useState(16);
  const [isSectionVisible] = useState(true);

  // modo "todos los viajes"
  const [mostrarTodos, setMostrarTodos] = useState(false);
  const [polylinesTodos, setPolylinesTodos] = useState([]);      // [{id, path, km}]
  const [distanciaTotalTodosKm, setDistanciaTotalTodosKm] = useState(0); // suma total
  const unsubsTodosRef = useRef([]); // para cortar listeners de todos

  // 1) Escuchar todos los viajes de ese LATAM-XX
  useEffect(() => {
    if (!viajeId) return;

    const unsub = ObtenerViajesPorLatamDocId(viajeId, (lista) => {
      console.log("Lista de viajes para", viajeId, lista);
      setViajesLista(lista);

      // Si no hay seleccionado aún, tomar el primero
      setViajeSeleccionadoId((prev) => {
        if (prev) return prev;
        return lista.length > 0 ? lista[0].id : null;
      });
    });

    return () => {
      if (unsub) unsub();
    };
  }, [viajeId]);

  // 2) Cuando cambia el viaje seleccionado y NO estamos en "todos", escuchar su GPS
  useEffect(() => {
    if (mostrarTodos) {
      setViaje(null);
      setPolylinePath([]);
      setDistanciaKm(0);
      return;
    }

    if (!viajeSeleccionadoId) {
      setViaje(null);
      setPolylinePath([]);
      setDistanciaKm(0);
      return;
    }

    setViaje(null);
    setPolylinePath([]);
    setDistanciaKm(0);

    const unsub = ObtenerUnViajeGPS(viajeSeleccionadoId, (data) => {
      console.log("Datos del viaje seleccionado:", viajeSeleccionadoId, data);
      setViaje(data);
    });

    return () => {
      if (unsub) unsub();
    };
  }, [viajeSeleccionadoId, mostrarTodos]);

  // 3) Construir el path del mapa cuando llegan los datos del viaje seleccionado
  useEffect(() => {
    if (mostrarTodos) {
      setPolylinePath([]);
      setDistanciaKm(0);
      return;
    }

    if (!viaje) {
      setPolylinePath([]);
      setDistanciaKm(0);
      return;
    }

    const gpsArray = viaje?.Datos?.gps || [];
    console.log("🛰 gpsArray en componente (seleccionado):", gpsArray);

    if (!gpsArray.length) {
      setPolylinePath([]);
      setDistanciaKm(0);
      return;
    }

    const path = gpsArray.map((p) => ({
      lat: p.latitude,
      lng: p.longitude,
    }));

    console.log("🗺 polyline path final (seleccionado):", path);
    setPolylinePath(path);

    // calcular distancia de este viaje
    const km = distanciaTotalKm(path);
    setDistanciaKm(km);

    // Centramos en el último punto
    setCenter(path[path.length - 1]);
    setZoom(16);
  }, [viaje, mostrarTodos]);

  // 4) MODO "TODOS": escuchar todos los viajes y armar sus paths + km
  useEffect(() => {
    // cortar listeners antiguos y limpiar estado
    unsubsTodosRef.current.forEach((fn) => fn && fn());
    unsubsTodosRef.current = [];
    setPolylinesTodos([]);
    setDistanciaTotalTodosKm(0);

    if (!mostrarTodos) return;
    if (!viajesLista.length) return;

    viajesLista.forEach((v) => {
      const unsub = ObtenerUnViajeGPS(v.id, (data) => {
        if (!data || !data.Datos || !Array.isArray(data.Datos.gps)) return;

        const path = data.Datos.gps.map((p) => ({
          lat: p.latitude,
          lng: p.longitude,
        }));

        const km = distanciaTotalKm(path);

        setPolylinesTodos((prev) => {
          const others = prev.filter((item) => item.id !== v.id);
          return [...others, { id: v.id, path, km }];
        });
      });

      unsubsTodosRef.current.push(unsub);
    });

    return () => {
      unsubsTodosRef.current.forEach((fn) => fn && fn());
      unsubsTodosRef.current = [];
    };
  }, [mostrarTodos, viajesLista]);

  // 4.1) Recalcular distancia total cuando cambian las polylines de todos
  useEffect(() => {
    if (!mostrarTodos) {
      setDistanciaTotalTodosKm(0);
      return;
    }
    const total = polylinesTodos.reduce((sum, item) => sum + (item.km || 0), 0);
    setDistanciaTotalTodosKm(total);
  }, [mostrarTodos, polylinesTodos]);

  // 5) Centrar mapa en modo "todos" cuando ya hay al menos una polyline
  useEffect(() => {
    if (!mostrarTodos) return;
    if (!polylinesTodos.length) return;

    const firstWithPoints = polylinesTodos.find((p) => p.path.length > 0);
    if (firstWithPoints) {
      setCenter(firstWithPoints.path[firstWithPoints.path.length - 1]);
      setZoom(13);
    }
  }, [mostrarTodos, polylinesTodos]);

  // Loader inicial
  if (!viaje && viajesLista.length === 0 && !mostrarTodos) {
    return <Typography sx={{ p: 2 }}>Buscando viajes para {viajeId}...</Typography>;
  }

  return (
    <>
      {/* AppBar */}
      <AppBar position="static" sx={{ marginBottom: 0, bgcolor: "#8c52fe" }}>
        <Container maxWidth="xl">
          <Toolbar disableGutters>
            <Grid container justifyContent="space-between" alignItems="center">
              <Grid item xs={6} sm={4} md={3}>
                <Typography variant="h6">
                  <img
                    src={Logo}
                    alt="Logo"
                    style={{ width: "80%", height: "80%", marginTop: "10px", marginBottom: "5px" }}
                  />
                </Typography>
              </Grid>
              <Grid alignItems="left">
                <Box
                  sx={{
                    p: 2,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    gap: 1,
                  }}
                >
                  <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                    LATAM: {viajeId}
                  </Typography>

                  {!mostrarTodos && viajeSeleccionadoId && (
                    <Typography variant="subtitle1">
                      Viaje seleccionado: {viajeSeleccionadoId}
                    </Typography>
                  )}

                  {!mostrarTodos && distanciaKm > 0 && (
                    <Typography variant="subtitle2">
                      Distancia recorrida aprox.: {distanciaKm.toFixed(2)} km
                    </Typography>
                  )}

                  {mostrarTodos && (
                    <>
                      <Typography variant="subtitle2">
                        Mostrando todos los viajes ({viajesLista.length})
                      </Typography>
                      {distanciaTotalTodosKm > 0 && (
                        <Typography variant="subtitle2">
                          Distancia total aprox.: {distanciaTotalTodosKm.toFixed(2)} km
                        </Typography>
                      )}
                    </>
                  )}

                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={() => setMostrarTodos((prev) => !prev)}
                  >
                    {mostrarTodos ? "Ver solo viaje seleccionado" : "Ver todos los viajes"}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Toolbar>
        </Container>
      </AppBar>

      <Box>
        <Grid container spacing={2} sx={{ height: "100%" }} alignItems="stretch">
          {/* Mapa */}
          <Grid item xs={12} sm={8.5} md={9.5} sx={{ height: "100%" }}>
            <LoadScriptNext googleMapsApiKey="AIzaSyAZJ7jCNaAU30Edy29CrsGGPwSdVLtY_qM">
              <GoogleMap
                mapContainerStyle={containerStyle}
                center={center}
                zoom={zoom}
                mapTypeId="roadmap"
              >
                {/* MODO 1: solo viaje seleccionado */}
                {!mostrarTodos && polylinePath.length > 0 && (
                  <>
                    <Polyline
                      key={viajeSeleccionadoId}
                      path={polylinePath}
                      options={{
                        strokeColor: "#FF0000",
                        strokeOpacity: 1,
                        strokeWeight: 5,
                      }}
                    />
                    <Marker
                      key={`${viajeSeleccionadoId}-start`}
                      position={polylinePath[0]}
                      icon={{ url: "https://maps.google.com/mapfiles/ms/icons/green-dot.png" }}
                    />
                    <Marker
                      key={`${viajeSeleccionadoId}-end`}
                      position={polylinePath[polylinePath.length - 1]}
                      icon={{ url: "https://maps.google.com/mapfiles/ms/icons/red-dot.png" }}
                    />
                  </>
                )}

                {/* MODO 2: todos los viajes */}
                {mostrarTodos &&
                  polylinesTodos.map(({ id, path }) =>
                    path.length > 0 ? (
                      <React.Fragment key={id}>
                        <Polyline
                          path={path}
                          options={{
                            strokeColor: "#FF0000",
                            strokeOpacity: 1,
                            strokeWeight: 4,
                          }}
                        />
                        <Marker
                          position={path[0]}
                          icon={{ url: "https://maps.google.com/mapfiles/ms/icons/green-dot.png" }}
                        />
                        <Marker
                          position={path[path.length - 1]}
                          icon={{ url: "https://maps.google.com/mapfiles/ms/icons/red-dot.png" }}
                        />
                      </React.Fragment>
                    ) : null
                  )}
              </GoogleMap>
            </LoadScriptNext>
          </Grid>

          {/* Panel lateral */}
          <Slide direction="right" in={isSectionVisible} mountOnEnter unmountOnExit>
            <Grid item xs={12} sm={3.5} md={2.5} sx={{ height: "100%" }}>
              <Card
                sx={{
                  boxShadow: 10,
                  textAlign: "left",
                  backgroundColor: grey[200],
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Paper
                  square
                  sx={{ flex: 1, overflow: "auto", backgroundColor: grey[200], p: 1 }}
                >
                  {/* Lista de viajes LATAM-XX-X */}
                  {viajesLista.map((v) => (
                    <HoverableCard
                      key={v.id}
                      isSelected={!mostrarTodos && viajeSeleccionadoId === v.id}
                      onClick={() => {
                        setViajeSeleccionadoId(v.id);
                        setMostrarTodos(false); // al hacer click vuelves a modo "uno"
                      }}
                    >
                      <Box sx={{ p: 1 }}>
                        <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                          {v.id}
                        </Typography>
                        {v.originAddress && (
                          <Typography variant="body2">
                            Origen: {v.originAddress}
                          </Typography>
                        )}
                        {v.destinationAddress && (
                          <Typography variant="body2">
                            Destino: {v.destinationAddress}
                          </Typography>
                        )}
                        <Typography variant="body2">Index: {v.viajeIndex}</Typography>
                      </Box>
                    </HoverableCard>
                  ))}

                  {/* Detalle del viaje seleccionado */}
                  {viaje && !mostrarTodos && (
                    <HoverableCard isSelected={false}>
                      <Box sx={{ p: 1 }}>
                        {viaje?.Datos?.driverId && (
                          <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                            Driver ID: {viaje.Datos.driverId}
                          </Typography>
                        )}

                        {viaje?.Datos?.originAddress && (
                          <Typography variant="body2">
                            Origen: {viaje.Datos.originAddress}
                          </Typography>
                        )}

                        {viaje?.Datos?.destinationAddress && (
                          <Typography variant="body2">
                            Destino: {viaje.Datos.destinationAddress}
                          </Typography>
                        )}

                        {viaje?.Datos?.scheduledDate && (
                          <Typography variant="body2">
                            Programado: {formatDateTime(viaje.Datos.scheduledDate)}
                          </Typography>
                        )}

                        {viaje?.Datos?.startedAt && (
                          <Typography variant="body2">
                            Inicio real: {formatDateTime(viaje.Datos.startedAt)}
                          </Typography>
                        )}

                        {viaje?.Datos?.endedAt && (
                          <Typography variant="body2">
                            Término: {formatDateTime(viaje.Datos.endedAt)}
                          </Typography>
                        )}

                        {distanciaKm > 0 && (
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            Distancia recorrida aprox.: {distanciaKm.toFixed(2)} km
                          </Typography>
                        )}
                      </Box>
                    </HoverableCard>
                  )}
                </Paper>
              </Card>
            </Grid>
          </Slide>
        </Grid>
      </Box>
    </>
  );
}
